'use client';

export type BreakEvenRoasInput = {
  salePrice: number;
  productCost: number;
  shippingCost: number;
  fees: number;
  targetProfit: number;
};

export type BreakEvenRoasResult = {
  salePrice: number;
  productCost: number;
  shippingCost: number;
  fees: number;
  targetProfit: number;
  nonAdCosts: number;
  contributionMargin: number;
  maxAdSpend: number;
  maxCac: number;
  breakEvenRoas: number;
  breakEvenRoasPercent: number;
};

export type LengthStatus = {
  label: 'Short' | 'Good' | 'Long';
  tone: 'warning' | 'success' | 'danger';
};

export type MetaLengthResult = {
  title: string;
  description: string;
  titleLength: number;
  descriptionLength: number;
  titleStatus: LengthStatus;
  descriptionStatus: LengthStatus;
  previewTitle: string;
  previewDescription: string;
  previewUrl: string;
};

export type AspectRatioPreset = {
  label: string;
  width: number;
  height: number;
};

export type AspectRatioInput = {
  width?: number | null;
  height?: number | null;
  ratioWidth?: number | null;
  ratioHeight?: number | null;
};

export type AspectRatioResult = {
  width: number | null;
  height: number | null;
  ratioWidth: number;
  ratioHeight: number;
  reducedRatio: string;
  decimalRatio: number;
  solvedWidth: number | null;
  solvedHeight: number | null;
  summary: string;
};

export type ParsedColor = {
  red: number;
  green: number;
  blue: number;
  alpha: number;
};

export type ColorConversionResult = {
  parsed: ParsedColor;
  hex: string;
  rgb: string;
  rgba: string;
  hsl: string;
  hsla: string;
  swatch: string;
};

export type TimestampConversionResult = {
  sourceKind: 'seconds' | 'milliseconds' | 'date';
  unixSeconds: number;
  unixMilliseconds: number;
  isoString: string;
  utcString: string;
  localString: string;
  timeZone: string;
};

export const aspectRatioPresets: AspectRatioPreset[] = [
  { label: '1:1', width: 1, height: 1 },
  { label: '4:5', width: 4, height: 5 },
  { label: '16:9', width: 16, height: 9 },
  { label: '9:16', width: 9, height: 16 },
  { label: '3:2', width: 3, height: 2 },
  { label: '2:3', width: 2, height: 3 },
];

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function gcd(left: number, right: number): number {
  let a = Math.abs(Math.round(left));
  let b = Math.abs(Math.round(right));

  while (b) {
    [a, b] = [b, a % b];
  }

  return a || 1;
}

function normalizeNumericInput(value: number | null | undefined) {
  if (value == null || Number.isNaN(value) || !Number.isFinite(value)) {
    return null;
  }

  return value;
}

function countVisibleCharacters(value: string) {
  return Array.from(value.trim()).length;
}

function getLengthStatus(length: number, shortMax: number, goodMax: number): LengthStatus {
  if (length === 0 || length <= shortMax) {
    return { label: 'Short', tone: 'warning' };
  }

  if (length <= goodMax) {
    return { label: 'Good', tone: 'success' };
  }

  return { label: 'Long', tone: 'danger' };
}

function truncateWithEllipsis(value: string, maxLength: number) {
  const chars = Array.from(value.trim());
  if (chars.length <= maxLength) {
    return value.trim();
  }

  return `${chars.slice(0, Math.max(maxLength - 1, 1)).join('')}…`;
}

function normalizeRatioPair(width: number, height: number) {
  const divisor = gcd(width, height);
  return {
    ratioWidth: Math.round(width / divisor),
    ratioHeight: Math.round(height / divisor),
  };
}

function formatColorChannel(value: number) {
  return Math.round(clamp(value, 0, 255));
}

function formatAlpha(alpha: number) {
  return Number(clamp(alpha, 0, 1).toFixed(3)).toString();
}

function componentToHex(value: number) {
  return formatColorChannel(value).toString(16).padStart(2, '0');
}

function parseAlphaToken(value: string | undefined) {
  if (!value) {
    return 1;
  }

  const trimmed = value.trim();
  if (trimmed.endsWith('%')) {
    return clamp(Number.parseFloat(trimmed) / 100, 0, 1);
  }

  return clamp(Number.parseFloat(trimmed), 0, 1);
}

function parseRgbChannel(value: string) {
  const trimmed = value.trim();
  if (trimmed.endsWith('%')) {
    return clamp((Number.parseFloat(trimmed) / 100) * 255, 0, 255);
  }

  return clamp(Number.parseFloat(trimmed), 0, 255);
}

function parseHueToken(value: string) {
  const numeric = Number.parseFloat(value.replace(/deg$/i, '').trim());
  if (!Number.isFinite(numeric)) {
    throw new Error('The color hue is invalid.');
  }

  const normalized = ((numeric % 360) + 360) % 360;
  return normalized;
}

function parsePercentage(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed.endsWith('%')) {
    throw new Error(`${label} must be provided as a percentage.`);
  }

  return clamp(Number.parseFloat(trimmed) / 100, 0, 1);
}

function splitFunctionArgs(value: string) {
  return value
    .replace(/\//g, ' ')
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function hslToRgb(hue: number, saturation: number, lightness: number) {
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const sector = hue / 60;
  const secondary = chroma * (1 - Math.abs((sector % 2) - 1));
  let red = 0;
  let green = 0;
  let blue = 0;

  if (sector >= 0 && sector < 1) {
    red = chroma;
    green = secondary;
  } else if (sector < 2) {
    red = secondary;
    green = chroma;
  } else if (sector < 3) {
    green = chroma;
    blue = secondary;
  } else if (sector < 4) {
    green = secondary;
    blue = chroma;
  } else if (sector < 5) {
    red = secondary;
    blue = chroma;
  } else {
    red = chroma;
    blue = secondary;
  }

  const offset = lightness - chroma / 2;

  return {
    red: (red + offset) * 255,
    green: (green + offset) * 255,
    blue: (blue + offset) * 255,
  };
}

function rgbToHsl(parsed: ParsedColor) {
  const red = clamp(parsed.red, 0, 255) / 255;
  const green = clamp(parsed.green, 0, 255) / 255;
  const blue = clamp(parsed.blue, 0, 255) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) {
    return { hue: 0, saturation: 0, lightness };
  }

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));

  let hue = 0;
  switch (max) {
    case red:
      hue = 60 * (((green - blue) / delta) % 6);
      break;
    case green:
      hue = 60 * ((blue - red) / delta + 2);
      break;
    default:
      hue = 60 * ((red - green) / delta + 4);
      break;
  }

  return {
    hue: ((hue % 360) + 360) % 360,
    saturation,
    lightness,
  };
}

function parseHexColor(value: string): ParsedColor | null {
  const normalized = value.trim().replace(/^#/, '');
  if (!/^[0-9a-f]{3,8}$/i.test(normalized) || ![3, 4, 6, 8].includes(normalized.length)) {
    return null;
  }

  const expanded =
    normalized.length === 3 || normalized.length === 4
      ? normalized
          .split('')
          .map((entry) => `${entry}${entry}`)
          .join('')
      : normalized;

  return {
    red: Number.parseInt(expanded.slice(0, 2), 16),
    green: Number.parseInt(expanded.slice(2, 4), 16),
    blue: Number.parseInt(expanded.slice(4, 6), 16),
    alpha: expanded.length === 8 ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : 1,
  };
}

function parseRgbLikeColor(value: string): ParsedColor | null {
  const match = value.trim().match(/^rgba?\((.*)\)$/i);
  if (!match) {
    return null;
  }

  const parts = splitFunctionArgs(match[1]);
  if (parts.length < 3 || parts.length > 4) {
    throw new Error('RGB and RGBA colors need 3 or 4 values.');
  }

  return {
    red: parseRgbChannel(parts[0]),
    green: parseRgbChannel(parts[1]),
    blue: parseRgbChannel(parts[2]),
    alpha: parseAlphaToken(parts[3]),
  };
}

function parseHslLikeColor(value: string): ParsedColor | null {
  const match = value.trim().match(/^hsla?\((.*)\)$/i);
  if (!match) {
    return null;
  }

  const parts = splitFunctionArgs(match[1]);
  if (parts.length < 3 || parts.length > 4) {
    throw new Error('HSL and HSLA colors need 3 or 4 values.');
  }

  const hue = parseHueToken(parts[0]);
  const saturation = parsePercentage(parts[1], 'Saturation');
  const lightness = parsePercentage(parts[2], 'Lightness');
  const rgb = hslToRgb(hue, saturation, lightness);

  return {
    red: rgb.red,
    green: rgb.green,
    blue: rgb.blue,
    alpha: parseAlphaToken(parts[3]),
  };
}

export function calculateBreakEvenRoas(input: BreakEvenRoasInput): BreakEvenRoasResult {
  if (input.salePrice <= 0) {
    throw new Error('Selling price must be greater than zero.');
  }

  if (
    [input.productCost, input.shippingCost, input.fees, input.targetProfit].some(
      (value) => value < 0,
    )
  ) {
    throw new Error('Costs and target profit cannot be negative.');
  }

  const nonAdCosts = input.productCost + input.shippingCost + input.fees;
  const contributionMargin = input.salePrice - nonAdCosts - input.targetProfit;

  if (contributionMargin <= 0) {
    throw new Error(
      'The contribution margin is zero or negative. Lower costs or target profit first.',
    );
  }

  const breakEvenRoas = input.salePrice / contributionMargin;

  return {
    ...input,
    nonAdCosts,
    contributionMargin,
    maxAdSpend: contributionMargin,
    maxCac: contributionMargin,
    breakEvenRoas,
    breakEvenRoasPercent: breakEvenRoas * 100,
  };
}

export function inspectMetaLengths(title: string, description: string): MetaLengthResult {
  const cleanTitle = title.trim();
  const cleanDescription = description.trim();

  if (!cleanTitle && !cleanDescription) {
    throw new Error('Add a title or description first.');
  }

  const titleLength = countVisibleCharacters(cleanTitle);
  const descriptionLength = countVisibleCharacters(cleanDescription);

  return {
    title: cleanTitle,
    description: cleanDescription,
    titleLength,
    descriptionLength,
    titleStatus: getLengthStatus(titleLength, 30, 60),
    descriptionStatus: getLengthStatus(descriptionLength, 70, 160),
    previewTitle: cleanTitle || 'Missing meta title',
    previewDescription: cleanDescription
      ? truncateWithEllipsis(cleanDescription, 160)
      : 'No meta description entered yet.',
    previewUrl: 'https://cleanor.app/example-page',
  };
}

export function calculateAspectRatio(input: AspectRatioInput): AspectRatioResult {
  const width = normalizeNumericInput(input.width ?? null);
  const height = normalizeNumericInput(input.height ?? null);
  const ratioWidthInput = normalizeNumericInput(input.ratioWidth ?? null);
  const ratioHeightInput = normalizeNumericInput(input.ratioHeight ?? null);

  if (
    (width == null || width <= 0) &&
    (height == null || height <= 0) &&
    (ratioWidthInput == null || ratioWidthInput <= 0)
  ) {
    throw new Error('Add width and height, or provide a ratio with one dimension.');
  }

  let ratioWidth = ratioWidthInput ?? null;
  let ratioHeight = ratioHeightInput ?? null;
  let solvedWidth: number | null = null;
  let solvedHeight: number | null = null;

  if (width != null && width > 0 && height != null && height > 0) {
    const reduced = normalizeRatioPair(width, height);
    ratioWidth = reduced.ratioWidth;
    ratioHeight = reduced.ratioHeight;
  }

  if (
    (ratioWidth == null || ratioHeight == null || ratioWidth <= 0 || ratioHeight <= 0) &&
    !(width != null && width > 0 && height != null && height > 0)
  ) {
    throw new Error('Add both ratio values, or add width and height first.');
  }

  if ((width == null || width <= 0) && height != null && height > 0) {
    solvedWidth = (height * (ratioWidth as number)) / (ratioHeight as number);
  }

  if ((height == null || height <= 0) && width != null && width > 0) {
    solvedHeight = (width * (ratioHeight as number)) / (ratioWidth as number);
  }

  if (
    (width == null || width <= 0) &&
    (height == null || height <= 0) &&
    ratioWidth &&
    ratioHeight
  ) {
    solvedWidth = ratioWidth;
    solvedHeight = ratioHeight;
  }

  const decimalRatio =
    (width != null && width > 0 ? width : (solvedWidth ?? ratioWidth ?? 1)) /
    (height != null && height > 0 ? height : (solvedHeight ?? ratioHeight ?? 1));

  const reducedRatio = `${Math.round(ratioWidth as number)}:${Math.round(ratioHeight as number)}`;
  const summary =
    solvedWidth && !width
      ? `At ${height} high, the matching width is ${Number(solvedWidth.toFixed(2))}.`
      : solvedHeight && !height
        ? `At ${width} wide, the matching height is ${Number(solvedHeight.toFixed(2))}.`
        : `The reduced ratio is ${reducedRatio}.`;

  return {
    width: width ?? null,
    height: height ?? null,
    ratioWidth: Math.round(ratioWidth as number),
    ratioHeight: Math.round(ratioHeight as number),
    reducedRatio,
    decimalRatio,
    solvedWidth: solvedWidth != null ? Number(solvedWidth.toFixed(2)) : null,
    solvedHeight: solvedHeight != null ? Number(solvedHeight.toFixed(2)) : null,
    summary,
  };
}

export function parseColorInput(value: string): ParsedColor {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error('Enter a color value first.');
  }

  const parsed = parseHexColor(trimmed) ?? parseRgbLikeColor(trimmed) ?? parseHslLikeColor(trimmed);

  if (!parsed) {
    throw new Error('Use a HEX, RGB, RGBA, HSL, or HSLA color value.');
  }

  return {
    red: formatColorChannel(parsed.red),
    green: formatColorChannel(parsed.green),
    blue: formatColorChannel(parsed.blue),
    alpha: clamp(parsed.alpha, 0, 1),
  };
}

export function convertColorFormats(value: string): ColorConversionResult {
  const parsed = parseColorInput(value);
  const hsl = rgbToHsl(parsed);
  const hue = Math.round(hsl.hue);
  const saturation = Math.round(hsl.saturation * 100);
  const lightness = Math.round(hsl.lightness * 100);

  return {
    parsed,
    hex: `#${componentToHex(parsed.red)}${componentToHex(parsed.green)}${componentToHex(parsed.blue)}`.toUpperCase(),
    rgb: `rgb(${parsed.red}, ${parsed.green}, ${parsed.blue})`,
    rgba: `rgba(${parsed.red}, ${parsed.green}, ${parsed.blue}, ${formatAlpha(parsed.alpha)})`,
    hsl: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
    hsla: `hsla(${hue}, ${saturation}%, ${lightness}%, ${formatAlpha(parsed.alpha)})`,
    swatch: `rgba(${parsed.red}, ${parsed.green}, ${parsed.blue}, ${formatAlpha(parsed.alpha)})`,
  };
}

export function convertUnixTimestampInput(
  timestampInput: string,
  dateInput: string,
): TimestampConversionResult {
  const trimmedTimestamp = timestampInput.trim();
  const trimmedDate = dateInput.trim();

  let sourceKind: TimestampConversionResult['sourceKind'];
  let date: Date;

  if (trimmedTimestamp) {
    if (/^-?\d+(\.\d+)?$/.test(trimmedTimestamp)) {
      const numeric = Number(trimmedTimestamp);
      const isMilliseconds = Math.abs(numeric) >= 1_000_000_000_000;
      sourceKind = isMilliseconds ? 'milliseconds' : 'seconds';
      date = new Date(isMilliseconds ? numeric : numeric * 1000);
    } else {
      sourceKind = 'date';
      date = new Date(trimmedTimestamp);
    }
  } else if (trimmedDate) {
    sourceKind = 'date';
    date = new Date(trimmedDate);
  } else {
    throw new Error('Enter a Unix timestamp or choose a date and time first.');
  }

  if (Number.isNaN(date.getTime())) {
    throw new Error('The timestamp or date input is invalid.');
  }

  const unixMilliseconds = date.getTime();
  const unixSeconds = Math.floor(unixMilliseconds / 1000);

  return {
    sourceKind,
    unixSeconds,
    unixMilliseconds,
    isoString: date.toISOString(),
    utcString: date.toUTCString(),
    localString: date.toLocaleString(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local time',
  };
}

export function createCalculatorJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}
